/**
 * EventsTab Component
 * 
 * FULLY FUNCTIONAL tournament hosting, event calendar, and registration system.
 * Uses Supabase for real data persistence and realtime updates.
 * Part of the Community Ecosystem (Part VI).
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Calendar, Trophy, Users, Clock, ChevronRight, ChevronDown,
    Plus, Filter, Swords, Crown, Grid3X3, RefreshCw, Zap, X,
    CheckCircle, AlertCircle, Play, Archive, Loader2, LogIn,
    LucideIcon, ExternalLink, Globe, MapPin, Sparkles, Star
} from 'lucide-react';
import { supabase, authService } from '../lib/supabase';
import { CommanderNight } from './CommanderNight';
import { SubNavDropdown } from './SubNavDropdown';

// Event Types
type EventType = 'swiss' | 'elimination' | 'round_robin' | 'league' | 'rumble';
type EventStatus = 'created' | 'registration' | 'checkin' | 'active' | 'completed' | 'archived';

interface Event {
    id: string;
    name: string;
    organizer_id: string;
    organizer_name: string;
    format: string;
    event_type: EventType;
    max_players: number;
    current_players: number;
    prize_pool?: string;
    start_time: string;
    registration_deadline: string;
    status: EventStatus;
    description: string;
    decklist_required: boolean;
    created_at: string;
}

interface EventRegistration {
    id: string;
    event_id: string;
    player_id: string;
    player_name: string;
    deck_name?: string;
    registered_at: string;
    checked_in: boolean;
}

// External aggregated events from edge function
interface ExternalEvent {
    id: string;
    name: string;
    date_start: string;
    date_end: string | null;
    location: string;
    url: string;
    source: string;
    source_icon: string;
    source_color: string;
    format: string;
    event_type: string;
    description: string;
    featured: boolean;
    tier: 'premier' | 'regional' | 'competitive' | 'local' | 'online';
}

// Tier styling config
const TIER_CONFIG: Record<string, { label: string; color: string; bgColor: string; borderColor: string; icon: LucideIcon }> = {
    premier: { label: 'Premier', color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', icon: Trophy },
    regional: { label: 'Regional', color: 'text-purple-400', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30', icon: Crown },
    competitive: { label: 'Competitive', color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30', icon: Swords },
    local: { label: 'Local', color: 'text-green-400', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30', icon: MapPin },
    online: { label: 'Online', color: 'text-cyan-400', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/30', icon: Globe },
};

// Event type configuration
const EVENT_TYPES: Record<EventType, { label: string; icon: LucideIcon; description: string }> = {
    swiss: {
        label: 'Swiss',
        icon: Grid3X3,
        description: 'Swiss rounds with cut to top 8',
    },
    elimination: {
        label: 'Single Elimination',
        icon: Trophy,
        description: 'Bracket tournament',
    },
    round_robin: {
        label: 'Round Robin',
        icon: RefreshCw,
        description: 'Everyone plays everyone',
    },
    league: {
        label: 'League',
        icon: Calendar,
        description: 'Ongoing matches over time period',
    },
    rumble: {
        label: 'DECKSTR Rumble',
        icon: Zap,
        description: 'Custom respawn-based format',
    },
};

// Status configuration
const STATUS_CONFIG: Record<EventStatus, { label: string; color: string; bgColor: string; icon: LucideIcon }> = {
    created: { label: 'Draft', color: 'text-gray-400', bgColor: 'bg-gray-500/20', icon: AlertCircle },
    registration: { label: 'Registration Open', color: 'text-green-400', bgColor: 'bg-green-500/20', icon: CheckCircle },
    checkin: { label: 'Check-in', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', icon: Clock },
    active: { label: 'In Progress', color: 'text-purple-400', bgColor: 'bg-purple-500/20', icon: Play },
    completed: { label: 'Completed', color: 'text-blue-400', bgColor: 'bg-blue-500/20', icon: Crown },
    archived: { label: 'Archived', color: 'text-gray-500', bgColor: 'bg-gray-500/10', icon: Archive },
};

const FORMATS = ['Standard', 'Modern', 'Pioneer', 'Legacy', 'Vintage', 'Commander', 'Pauper', 'Draft', 'Sealed', 'Other'];

function formatEventTime(dateString: string): { date: string; time: string; relative: string } {
    const date = new Date(dateString);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    let relative = '';
    if (diff < 0) {
        relative = 'Started';
    } else if (hours < 24) {
        relative = hours <= 1 ? 'Starting soon' : `In ${hours}h`;
    } else if (days < 7) {
        relative = `In ${days} days`;
    } else {
        relative = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    return {
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        relative,
    };
}

// ============================================
// CREATE EVENT MODAL
// ============================================
interface CreateEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (event: Event) => void;
    userId: string;
    userName: string;
}

function CreateEventModal({ isOpen, onClose, onCreated, userId, userName }: CreateEventModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [name, setName] = useState('');
    const [format, setFormat] = useState('Standard');
    const [eventType, setEventType] = useState<EventType>('swiss');
    const [maxPlayers, setMaxPlayers] = useState(32);
    const [prizePool, setPrizePool] = useState('');
    const [description, setDescription] = useState('');
    const [decklistRequired, setDecklistRequired] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('14:00');
    const [regDeadlineHours, setRegDeadlineHours] = useState(1);

    // Set default date to tomorrow when modal opens
    useEffect(() => {
        if (isOpen && !startDate) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            setStartDate(tomorrow.toISOString().split('T')[0]);
        }
    }, [isOpen, startDate]);

    // Early return AFTER all hooks
    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // Combine date and time
            const startDateTime = new Date(`${startDate}T${startTime}`);
            const regDeadline = new Date(startDateTime.getTime() - regDeadlineHours * 60 * 60 * 1000);

            const { data, error: insertError } = await supabase
                .from('events')
                .insert({
                    name,
                    organizer_id: userId,
                    organizer_name: userName,
                    format,
                    event_type: eventType,
                    max_players: maxPlayers,
                    current_players: 0,
                    prize_pool: prizePool || null,
                    start_time: startDateTime.toISOString(),
                    registration_deadline: regDeadline.toISOString(),
                    status: 'registration',
                    description,
                    decklist_required: decklistRequired,
                })
                .select()
                .single();

            if (insertError) throw insertError;

            onCreated(data);
            onClose();

            // Reset form
            setName('');
            setDescription('');
            setPrizePool('');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to create event');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-lg max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-purple-400" />
                        Create Event
                    </h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white p-1">
                        <X className="size-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Event Name */}
                    <div>
                        <label className="block text-sm text-white/60 mb-1">Event Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            placeholder="Weekend Warrior Standard"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500"
                        />
                    </div>

                    {/* Format & Type */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm text-white/60 mb-1">Format *</label>
                            <select
                                value={format}
                                onChange={(e) => setFormat(e.target.value)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                            >
                                {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm text-white/60 mb-1">Type *</label>
                            <select
                                value={eventType}
                                onChange={(e) => setEventType(e.target.value as EventType)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                            >
                                {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Max Players & Prize */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm text-white/60 mb-1">Max Players *</label>
                            <select
                                value={maxPlayers}
                                onChange={(e) => setMaxPlayers(Number(e.target.value))}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                            >
                                {[8, 16, 32, 64, 128, 256].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm text-white/60 mb-1">Prize Pool</label>
                            <input
                                type="text"
                                value={prizePool}
                                onChange={(e) => setPrizePool(e.target.value)}
                                placeholder="$100 store credit"
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500"
                            />
                        </div>
                    </div>

                    {/* Date & Time */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm text-white/60 mb-1">Start Date *</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-white/60 mb-1">Start Time *</label>
                            <input
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                required
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                            />
                        </div>
                    </div>

                    {/* Reg Deadline */}
                    <div>
                        <label className="block text-sm text-white/60 mb-1">Registration closes</label>
                        <select
                            value={regDeadlineHours}
                            onChange={(e) => setRegDeadlineHours(Number(e.target.value))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                        >
                            <option value={0}>At start time</option>
                            <option value={0.5}>30 minutes before</option>
                            <option value={1}>1 hour before</option>
                            <option value={2}>2 hours before</option>
                            <option value={24}>1 day before</option>
                        </select>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm text-white/60 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Tell players about your event..."
                            rows={3}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500 resize-none"
                        />
                    </div>

                    {/* Options */}
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={decklistRequired}
                                onChange={(e) => setDecklistRequired(e.target.checked)}
                                className="rounded border-white/20 bg-white/5 text-purple-500 focus:ring-purple-500"
                            />
                            Require decklist submission
                        </label>
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-white/60 hover:text-white"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !name || !startDate}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Create Event
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ============================================
// ROSTER MODAL - View registered players
// ============================================
interface RosterModalProps {
    isOpen: boolean;
    onClose: () => void;
    event: Event;
}

interface Registration {
    id: string;
    player_id: string;
    player_name: string;
    deck_name: string | null;
    deck_id: string | null;
    registered_at: string;
    checked_in: boolean;
}

function RosterModal({ isOpen, onClose, event }: RosterModalProps) {
    const [registrations, setRegistrations] = useState<Registration[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isOpen) return;

        const fetchRoster = async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('event_registrations')
                .select('*')
                .eq('event_id', event.id)
                .order('registered_at', { ascending: true });

            if (!error && data) {
                setRegistrations(data);
            }
            setLoading(false);
        };

        fetchRoster();

        // Subscribe to realtime changes
        const channel = supabase
            .channel(`roster-${event.id}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'event_registrations',
                filter: `event_id=eq.${event.id}`,
            }, () => {
                fetchRoster();
            })
            .subscribe();

        return () => { channel.unsubscribe(); };
    }, [isOpen, event.id]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-lg max-h-[80vh] overflow-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-400" />
                        Event Roster
                    </h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white p-1">
                        <X className="size-5" />
                    </button>
                </div>

                <div className="mb-4 p-3 bg-white/5 rounded-lg">
                    <div className="text-lg font-semibold text-white">{event.name}</div>
                    <div className="text-sm text-white/60">{event.format} • {event.current_players}/{event.max_players} players</div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                    </div>
                ) : registrations.length === 0 ? (
                    <div className="text-center py-8 text-white/40">
                        No players registered yet
                    </div>
                ) : (
                    <div className="space-y-2">
                        {registrations.map((reg, index) => (
                            <div key={reg.id} className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-white/30 w-6">{index + 1}.</span>
                                    <div>
                                        <div className="text-sm font-medium text-white">{reg.player_name}</div>
                                        {reg.deck_name && (
                                            <div className="text-xs text-purple-400">{reg.deck_name}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {reg.checked_in && (
                                        <span className="text-xs text-green-400 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" /> Checked In
                                        </span>
                                    )}
                                    {reg.deck_id && (
                                        <button className="text-xs text-purple-400 hover:text-purple-300">
                                            View Deck
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================
// REGISTER MODAL - Deck submission for registration
// ============================================
interface RegisterModalProps {
    isOpen: boolean;
    onClose: () => void;
    event: Event;
    userName: string;
    onRegistered: () => void;
}

interface UserDeck {
    id: string;
    name: string;
    format: string;
}

function RegisterModal({ isOpen, onClose, event, userName, onRegistered }: RegisterModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [myDecks, setMyDecks] = useState<UserDeck[]>([]);
    const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
    const [loadingDecks, setLoadingDecks] = useState(true);

    useEffect(() => {
        if (!isOpen) return;

        const fetchMyDecks = async () => {
            setLoadingDecks(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error: fetchError } = await supabase
                .from('decks')
                .select('id, name, format')
                .eq('user_id', user.id)
                .order('updated_at', { ascending: false });

            if (!fetchError && data) {
                setMyDecks(data);
                // Auto-select if matching format
                const matchingDeck = data.find(d => d.format?.toLowerCase() === event.format?.toLowerCase());
                if (matchingDeck) setSelectedDeckId(matchingDeck.id);
            }
            setLoadingDecks(false);
        };

        fetchMyDecks();
    }, [isOpen, event.format]);

    if (!isOpen) return null;

    const handleRegister = async () => {
        setError(null);
        setLoading(true);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const selectedDeck = myDecks.find(d => d.id === selectedDeckId);

            const { error: insertError } = await supabase
                .from('event_registrations')
                .insert({
                    event_id: event.id,
                    player_id: user.id,
                    player_name: userName,
                    deck_id: selectedDeckId,
                    deck_name: selectedDeck?.name || null,
                });

            if (insertError) throw insertError;

            // Update player count
            await supabase
                .from('events')
                .update({ current_players: event.current_players + 1 })
                .eq('id', event.id);

            onRegistered();
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to register');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0f172a] rounded-xl border border-white/20 p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Plus className="w-5 h-5 text-purple-400" />
                        Register for Event
                    </h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white p-1">
                        <X className="size-5" />
                    </button>
                </div>

                <div className="mb-4 p-3 bg-white/5 rounded-lg">
                    <div className="text-lg font-semibold text-white">{event.name}</div>
                    <div className="text-sm text-white/60">{event.format} • {event.current_players}/{event.max_players} players</div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                        {error}
                    </div>
                )}

                {event.decklist_required && (
                    <div className="mb-4">
                        <label className="block text-sm text-white/60 mb-2">
                            Select Deck <span className="text-red-400">*</span>
                        </label>
                        {loadingDecks ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                            </div>
                        ) : myDecks.length === 0 ? (
                            <div className="text-sm text-yellow-400 p-3 bg-yellow-500/10 rounded-lg">
                                You don't have any saved decks. Create a deck in the BREW tab first.
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-48 overflow-auto">
                                {myDecks.map(deck => (
                                    <button
                                        key={deck.id}
                                        onClick={() => setSelectedDeckId(deck.id)}
                                        className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedDeckId === deck.id
                                            ? 'border-purple-500 bg-purple-500/20 text-white'
                                            : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                                            }`}
                                    >
                                        <div className="font-medium">{deck.name}</div>
                                        <div className="text-xs text-white/40">{deck.format}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {!event.decklist_required && (
                    <div className="mb-4">
                        <label className="block text-sm text-white/60 mb-2">
                            Select Deck (Optional)
                        </label>
                        {loadingDecks ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                            </div>
                        ) : myDecks.length === 0 ? (
                            <div className="text-sm text-white/40 p-3 bg-white/5 rounded-lg">
                                No saved decks. You can register without one.
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-48 overflow-auto">
                                <button
                                    onClick={() => setSelectedDeckId(null)}
                                    className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedDeckId === null
                                        ? 'border-purple-500 bg-purple-500/20 text-white'
                                        : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                                        }`}
                                >
                                    <div className="font-medium">No deck selected</div>
                                </button>
                                {myDecks.map(deck => (
                                    <button
                                        key={deck.id}
                                        onClick={() => setSelectedDeckId(deck.id)}
                                        className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedDeckId === deck.id
                                            ? 'border-purple-500 bg-purple-500/20 text-white'
                                            : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                                            }`}
                                    >
                                        <div className="font-medium">{deck.name}</div>
                                        <div className="text-xs text-white/40">{deck.format}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-white/60 hover:text-white">
                        Cancel
                    </button>
                    <button
                        onClick={handleRegister}
                        disabled={loading || (event.decklist_required && !selectedDeckId)}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white rounded-lg"
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Register
                    </button>
                </div>
            </div>
        </div>
    );
}

// ============================================
// EVENT CARD COMPONENT
// ============================================
interface EventCardProps {
    event: Event;
    isRegistered: boolean;
    onRegister: (eventId: string, deckRequired: boolean) => void;
    onWithdraw: (eventId: string) => void;
    isLoading: boolean;
    isLoggedIn: boolean;
    onViewRoster: (event: Event) => void;
    isOrganizer: boolean;
    onDelete: (eventId: string) => void;
}

function EventCard({ event, isRegistered, onRegister, onWithdraw, isLoading, isLoggedIn, onViewRoster, isOrganizer, onDelete }: EventCardProps) {
    const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.swiss;
    const statusConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.created;
    const timeInfo = formatEventTime(event.start_time);
    const TypeIcon = typeConfig.icon;
    const StatusIcon = statusConfig.icon;

    const spotsLeft = event.max_players - event.current_players;
    const isFull = spotsLeft === 0;
    const isOpen = event.status === 'registration';

    return (
        <div className="group p-4 bg-slate-800/50 rounded-xl border border-white/5 hover:border-white/15 transition-all">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${statusConfig.bgColor}`}>
                        <TypeIcon className={`w-5 h-5 ${statusConfig.color}`} />
                    </div>
                    <div>
                        <div className={`flex items-center gap-1.5 text-xs ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                        </div>
                        <div className="text-xs text-white/40">{typeConfig.label}</div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-sm font-medium text-white">{timeInfo.relative}</div>
                    <div className="text-xs text-white/40">{timeInfo.date}</div>
                </div>
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-white group-hover:text-purple-300 transition-colors mb-2">
                {event.name}
            </h3>

            {/* Description */}
            {event.description && (
                <p className="text-sm text-white/60 mb-3 line-clamp-2">
                    {event.description}
                </p>
            )}

            {/* Meta Row */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-white/50 mb-3">
                <div className="flex items-center gap-1">
                    <Swords className="w-3 h-3" />
                    {event.format}
                </div>
                <div className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {event.current_players}/{event.max_players}
                    {spotsLeft > 0 && spotsLeft <= 5 && (
                        <span className="text-amber-400">({spotsLeft} left)</span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeInfo.time}
                </div>
            </div>

            {/* Prize Pool */}
            {event.prize_pool && (
                <div className="flex items-center gap-1 text-xs text-yellow-400 mb-3">
                    <Trophy className="w-3 h-3" />
                    {event.prize_pool}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <div className="flex items-center gap-2">
                    <span className="text-xs text-white/40">By {event.organizer_name}</span>
                    {/* View Roster button */}
                    <button
                        onClick={() => onViewRoster(event)}
                        className="text-xs text-purple-400 hover:text-purple-300"
                    >
                        Roster ({event.current_players})
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    {/* Organizer/Admin controls */}
                    {isOrganizer && (
                        <button
                            onClick={() => {
                                if (confirm(`Delete event "${event.name}"? This cannot be undone.`)) {
                                    onDelete(event.id);
                                }
                            }}
                            className="text-xs text-red-400/70 hover:text-red-400"
                        >
                            Delete
                        </button>
                    )}

                    {!isLoggedIn && isOpen && (
                        <button className="flex items-center gap-1 px-3 py-1.5 bg-white/10 text-white/60 text-xs rounded-lg cursor-not-allowed">
                            <LogIn className="w-3 h-3" />
                            Sign in to register
                        </button>
                    )}

                    {isLoggedIn && isOpen && !isRegistered && !isFull && (
                        <button
                            onClick={() => onRegister(event.id, event.decklist_required)}
                            disabled={isLoading}
                            className="flex items-center gap-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white text-xs rounded-lg transition-colors"
                        >
                            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                            Register
                        </button>
                    )}

                    {isLoggedIn && isOpen && isRegistered && (
                        <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1 text-xs text-green-400">
                                <CheckCircle className="w-3 h-3" />
                                Registered
                            </span>
                            <button
                                onClick={() => onWithdraw(event.id)}
                                disabled={isLoading}
                                className="text-xs text-red-400/70 hover:text-red-400"
                            >
                                Withdraw
                            </button>
                        </div>
                    )}

                    {isOpen && isFull && !isRegistered && (
                        <span className="text-xs text-red-400">Full</span>
                    )}

                    {event.status === 'active' && (
                        <button className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 text-purple-300 text-xs rounded-lg">
                            Watch Live <Play className="w-3 h-3" />
                        </button>
                    )}

                    {event.status === 'completed' && (
                        <button className="flex items-center gap-1 text-xs text-white/50 hover:text-white">
                            View Results <ChevronRight className="w-3 h-3" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================
// MAIN EVENTS TAB COMPONENT
// ============================================
export function EventsTab() {
    // Auth state
    const [user, setUser] = useState<{ id: string; email: string } | null>(null);
    const [userName, setUserName] = useState('');

    // Data state
    const [events, setEvents] = useState<Event[]>([]);
    const [myRegistrations, setMyRegistrations] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    // External events state
    const [externalEvents, setExternalEvents] = useState<ExternalEvent[]>([]);
    const [loadingExternal, setLoadingExternal] = useState(true);
    const [externalError, setExternalError] = useState<string | null>(null);
    const [selectedTier, setSelectedTier] = useState<string | null>(null);

    // UI state
    const [selectedFormat, setSelectedFormat] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<EventStatus | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Modal state
    const [rosterEvent, setRosterEvent] = useState<Event | null>(null);
    const [registerEvent, setRegisterEvent] = useState<Event | null>(null);

    // Check auth status
    useEffect(() => {
        const checkAuth = async () => {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
                setUser({ id: currentUser.id, email: currentUser.email || '' });
                // Try to get display name from profile
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('display_name')
                    .eq('id', currentUser.id)
                    .single();
                setUserName(profile?.display_name || currentUser.email?.split('@')[0] || 'Player');
            }
        };
        checkAuth();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setUser({ id: session.user.id, email: session.user.email || '' });
            } else {
                setUser(null);
                setMyRegistrations(new Set());
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Fetch events
    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, error: fetchError } = await supabase
                .from('events')
                .select('*')
                .order('start_time', { ascending: true });

            if (fetchError) {
                // Check if table doesn't exist
                if (fetchError.code === '42P01' || fetchError.message?.includes('does not exist')) {
                    setError('Database tables not set up. Please run the SQL migration in Supabase Dashboard.');
                } else {
                    throw fetchError;
                }
                return;
            }
            setEvents(data || []);
        } catch (err: unknown) {
            console.error('Failed to fetch events:', err);
            setError('Failed to load events. Check console for details.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch my registrations
    const fetchMyRegistrations = useCallback(async () => {
        if (!user) return;

        try {
            const { data, error: fetchError } = await supabase
                .from('event_registrations')
                .select('event_id')
                .eq('player_id', user.id);

            if (fetchError) throw fetchError;

            const regSet = new Set(data?.map(r => r.event_id) || []);
            setMyRegistrations(regSet);
        } catch (err) {
            console.error('Failed to fetch registrations:', err);
        }
    }, [user]);

    // Fetch external aggregated events
    const fetchExternalEvents = useCallback(async () => {
        setLoadingExternal(true);
        setExternalError(null);
        try {
            const SUPABASE_URL = 'https://yytoqcneczoxwegwtuep.supabase.co';
            const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl5dG9xY25lY3pveHdlZ3d0dWVwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgwODM1MDgsImV4cCI6MjA4MzY1OTUwOH0.AapY3VenT2om6UHhxa5KmM4UxeilQ_DRsct2ubzi1Gs';

            const response = await fetch(`${SUPABASE_URL}/functions/v1/events-aggregator`, {
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': SUPABASE_ANON_KEY,
                },
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setExternalEvents(data.events || []);
        } catch (err: unknown) {
            console.error('Failed to fetch external events:', err);
            setExternalError('Could not load event calendar.');
        } finally {
            setLoadingExternal(false);
        }
    }, []);

    // Status sort order: ongoing (always available) → upcoming → live → ended
    const statusSortOrder = (event: ExternalEvent): number => {
        const now = new Date();
        const startDate = new Date(event.date_start);
        const endDate = event.date_end ? new Date(event.date_end) : startDate;
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);

        // Recurring (local/online with no end date) = ongoing → FIRST
        if ((event.tier === 'local' || event.tier === 'online') && !event.date_end) return 0;
        // Ended → LAST
        if (today > endDay) return 3;
        // Live (happening now)
        if (today >= startDay && today <= endDay) return 2;
        // Upcoming
        return 1;
    };

    const sortEvents = (a: ExternalEvent, b: ExternalEvent) => {
        const statusDiff = statusSortOrder(a) - statusSortOrder(b);
        if (statusDiff !== 0) return statusDiff;
        // Within same status, sort by date (soonest first for upcoming, most recent first for ended)
        return new Date(a.date_start).getTime() - new Date(b.date_start).getTime();
    };

    // Filter external events by tier
    const filteredExternalEvents = useMemo(() => {
        const filtered = selectedTier ? externalEvents.filter(e => e.tier === selectedTier) : externalEvents;
        return [...filtered].sort(sortEvents);
    }, [externalEvents, selectedTier]);

    const featuredExternalEvents = useMemo(() => {
        return [...externalEvents.filter(e => e.featured)].sort(sortEvents);
    }, [externalEvents]);

    const nonFeaturedExternalEvents = useMemo(() => {
        const filtered = selectedTier
            ? externalEvents.filter(e => !e.featured && e.tier === selectedTier)
            : externalEvents.filter(e => !e.featured);
        return [...filtered].sort(sortEvents);
    }, [externalEvents, selectedTier]);

    // Initial fetch
    useEffect(() => {
        fetchEvents();
        fetchExternalEvents();
    }, [fetchEvents, fetchExternalEvents]);

    // Fetch registrations when user changes
    useEffect(() => {
        fetchMyRegistrations();
    }, [fetchMyRegistrations]);

    // Realtime subscription
    useEffect(() => {
        const channel = supabase
            .channel('events-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, (payload) => {
                if (payload.eventType === 'INSERT') {
                    setEvents(prev => [...prev, payload.new as Event]);
                } else if (payload.eventType === 'UPDATE') {
                    setEvents(prev => prev.map(e => e.id === payload.new.id ? payload.new as Event : e));
                } else if (payload.eventType === 'DELETE') {
                    setEvents(prev => prev.filter(e => e.id !== payload.old.id));
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    // Register for event - open modal if decklist required, else register directly
    const handleRegister = async (eventId: string, deckRequired: boolean = false) => {
        if (!user) return;

        // Find the event
        const event = events.find(e => e.id === eventId);
        if (!event) return;

        // If decklist required or user might want to pick a deck, show modal
        if (deckRequired) {
            setRegisterEvent(event);
            return;
        }

        // Direct registration without deck
        setActionLoading(eventId);

        try {
            const { error: regError } = await supabase
                .from('event_registrations')
                .insert({
                    event_id: eventId,
                    player_id: user.id,
                    player_name: userName,
                });

            if (regError) throw regError;

            // Update local count
            setEvents(prev => prev.map(e =>
                e.id === eventId ? { ...e, current_players: e.current_players + 1 } : e
            ));
            setMyRegistrations(prev => new Set([...prev, eventId]));
        } catch (err: unknown) {
            console.error('Failed to register:', err);
            alert(err instanceof Error ? err.message : 'Failed to register');
        } finally {
            setActionLoading(null);
        }
    };

    // Withdraw from event
    const handleWithdraw = async (eventId: string) => {
        if (!user) return;
        if (!confirm('Are you sure you want to withdraw from this event?')) return;

        setActionLoading(eventId);

        try {
            const { error: delError } = await supabase
                .from('event_registrations')
                .delete()
                .eq('event_id', eventId)
                .eq('player_id', user.id);

            if (delError) throw delError;

            // Update local count
            setEvents(prev => prev.map(e =>
                e.id === eventId ? { ...e, current_players: Math.max(0, e.current_players - 1) } : e
            ));
            setMyRegistrations(prev => {
                const newSet = new Set(prev);
                newSet.delete(eventId);
                return newSet;
            });
        } catch (err: unknown) {
            console.error('Failed to withdraw:', err);
            alert(err instanceof Error ? err.message : 'Failed to withdraw');
        } finally {
            setActionLoading(null);
        }
    };

    // Delete event (organizer or admin only)
    const handleDelete = async (eventId: string) => {
        if (!user) return;
        setActionLoading(eventId);

        try {
            const { error: deleteError } = await supabase
                .from('events')
                .delete()
                .eq('id', eventId);

            if (deleteError) throw deleteError;

            // Remove from local state
            setEvents(prev => prev.filter(e => e.id !== eventId));
        } catch (err: unknown) {
            console.error('Failed to delete:', err);
            alert(err instanceof Error ? err.message : 'Failed to delete event');
        } finally {
            setActionLoading(null);
        }
    };

    // Filter events
    const filteredEvents = useMemo(() => {
        let filtered = events;
        if (selectedFormat) {
            filtered = filtered.filter(e => e.format.toLowerCase() === selectedFormat.toLowerCase());
        }
        if (selectedStatus) {
            filtered = filtered.filter(e => e.status === selectedStatus);
        }
        return filtered;
    }, [events, selectedFormat, selectedStatus]);

    // Get unique formats from events
    const formats = useMemo(() => {
        return [...new Set(events.map(e => e.format))];
    }, [events]);

    // Determine event status: live, upcoming, ended, or ongoing (recurring)
    const getEventStatus = (start: string, end: string | null, tier: string): { status: 'live' | 'upcoming' | 'ended' | 'ongoing'; label: string; color: string; bgColor: string; pulse: boolean } => {
        // Recurring events (local/online tier with no real end date) are always "ongoing"
        if ((tier === 'local' || tier === 'online') && !end) {
            return { status: 'ongoing', label: 'Always Available', color: 'text-emerald-400', bgColor: 'bg-emerald-500/15', pulse: false };
        }

        const now = new Date();
        const startDate = new Date(start);
        const endDate = end ? new Date(end) : startDate;

        // Set start to beginning of day, end to end of day for inclusive comparison
        const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        if (today > endDay) {
            return { status: 'ended', label: 'Ended', color: 'text-white/40', bgColor: 'bg-white/5', pulse: false };
        }
        if (today >= startDay && today <= endDay) {
            return { status: 'live', label: 'Happening Now', color: 'text-green-400', bgColor: 'bg-green-500/15', pulse: true };
        }

        const diffMs = startDay.getTime() - today.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays <= 1) {
            return { status: 'upcoming', label: 'Tomorrow', color: 'text-amber-400', bgColor: 'bg-amber-500/15', pulse: false };
        }
        if (diffDays <= 7) {
            return { status: 'upcoming', label: `In ${diffDays} days`, color: 'text-blue-400', bgColor: 'bg-blue-500/15', pulse: false };
        }
        return { status: 'upcoming', label: `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, color: 'text-white/50', bgColor: 'bg-white/5', pulse: false };
    };

    // Format external event date nicely
    const formatExternalDate = (start: string, end: string | null) => {
        const s = new Date(start);
        const dateStr = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (end) {
            const e = new Date(end);
            const endStr = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            if (dateStr !== endStr) return `${dateStr} – ${endStr}`;
        }
        return dateStr;
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 p-6">
            {/* Commander Night - Featured Event */}
            <CommanderNight />

            {/* ══════════════════════════════════════ */}
            {/* UPCOMING EVENTS - External Aggregator */}
            {/* ══════════════════════════════════════ */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Sparkles className="w-6 h-6 text-amber-400" />
                        <div>
                            <h2 className="text-xl font-bold text-white">Upcoming Events</h2>
                            <p className="text-sm text-white/50">
                                {loadingExternal ? 'Loading...' : `${externalEvents.length} events from official sources`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Tier Filter Dropdown */}
                        <SubNavDropdown
                            items={[
                                { key: '__all__', label: 'All Tiers', icon: Calendar, color: 'text-white' },
                                ...Object.entries(TIER_CONFIG).map(([key, config]) => ({
                                    key,
                                    label: config.label,
                                    icon: config.icon,
                                    color: config.color,
                                    count: externalEvents.filter(e => e.tier === key).length,
                                })),
                            ]}
                            activeKey={selectedTier || '__all__'}
                            onSelect={(key: string) => setSelectedTier(key === '__all__' ? null : key)}
                        />
                        <button
                            onClick={fetchExternalEvents}
                            disabled={loadingExternal}
                            className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs font-medium transition-colors border border-purple-500/20"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loadingExternal ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Loading state */}
                {loadingExternal && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                    </div>
                )}

                {/* Error state */}
                {externalError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
                        {externalError}
                    </div>
                )}

                {/* All Events Grid - Unified & Sorted */}
                {!loadingExternal && filteredExternalEvents.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filteredExternalEvents.map(event => {
                            const tierConfig = TIER_CONFIG[event.tier] || TIER_CONFIG.competitive;
                            const TierIcon = tierConfig.icon;
                            const eventStatus = getEventStatus(event.date_start, event.date_end, event.tier);
                            const isEnded = eventStatus.status === 'ended';
                            const isFeatured = event.featured;

                            return (
                                <a
                                    key={event.id}
                                    href={event.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`group ${isFeatured
                                        ? `relative overflow-hidden p-4 rounded-xl border transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/10 bg-gradient-to-br ${isEnded ? 'from-slate-800/30 via-slate-800/30 to-slate-800/30 opacity-60' : 'from-amber-900/20 via-slate-800/50 to-purple-900/20'} ${tierConfig.borderColor}`
                                        : `p-3 rounded-lg border transition-all hover:scale-[1.01] ${isEnded ? 'bg-slate-900/30 opacity-50' : 'bg-slate-800/40 hover:bg-slate-800/60'} ${tierConfig.borderColor}`
                                        }`}
                                >
                                    <div className="flex items-start justify-between mb-1">
                                        <div className={`flex items-center gap-1.5 text-[10px] font-${isFeatured ? 'semibold uppercase tracking-wider' : 'medium'} ${tierConfig.color}`}>
                                            {isFeatured ? <Star className="w-3 h-3" /> : <TierIcon className="w-3 h-3" />}
                                            {event.event_type}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full ${eventStatus.bgColor} ${eventStatus.color}`}>
                                                {eventStatus.pulse && <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" /></span>}
                                                {eventStatus.label}
                                            </span>
                                            {isFeatured && <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-white/60 transition-colors" />}
                                        </div>
                                    </div>
                                    <h3 className={`text-sm font-${isFeatured ? 'bold' : 'medium'} mb-1 line-clamp-${isFeatured ? '2' : '1'} transition-colors ${isEnded ? 'text-white/50' : `text-white group-hover:text-${isFeatured ? 'amber-200' : 'white/90'}`}`}>
                                        {event.name}
                                    </h3>
                                    <div className="flex items-center gap-3 text-[11px] text-white/50 mb-2">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {formatExternalDate(event.date_start, event.date_end)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 text-[10px] text-white/35">
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-2.5 h-2.5" />
                                            {event.location}
                                        </span>
                                        {!isFeatured && (
                                            <span className="flex items-center gap-1">
                                                <Swords className="w-2.5 h-2.5" />
                                                {event.format}
                                            </span>
                                        )}
                                    </div>
                                    {isFeatured && !isEnded && <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />}
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Community Events Section - only show when there are actual community events */}
            {events.length > 0 && (
                <>
                    <div className="border-t border-white/5 pt-4 mt-2">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Calendar className="w-5 h-5 text-purple-400" />
                                <div>
                                    <h2 className="text-lg font-bold text-white">Community Events</h2>
                                    <p className="text-xs text-white/40">Tournaments, leagues, and community events</p>
                                </div>
                            </div>
                            {user && (
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg transition-colors"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Create Event</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-white/40" />
                            <select
                                value={selectedFormat || ''}
                                onChange={(e) => setSelectedFormat(e.target.value || null)}
                                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500"
                            >
                                <option value="">All Formats</option>
                                {formats.map(format => (
                                    <option key={format} value={format}>{format}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setSelectedStatus(null)}
                                className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${selectedStatus === null
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-white/5 text-white/60 hover:text-white'
                                    }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setSelectedStatus('registration')}
                                className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${selectedStatus === 'registration'
                                    ? 'bg-green-500/30 text-green-300'
                                    : 'bg-white/5 text-white/60 hover:text-white'
                                    }`}
                            >
                                Open
                            </button>
                            <button
                                onClick={() => setSelectedStatus('active')}
                                className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${selectedStatus === 'active'
                                    ? 'bg-purple-500/30 text-purple-300'
                                    : 'bg-white/5 text-white/60 hover:text-white'
                                    }`}
                            >
                                Live
                            </button>
                        </div>
                        <button
                            onClick={fetchEvents}
                            disabled={loading}
                            className="ml-auto flex items-center gap-1 text-xs text-white/40 hover:text-white/60"
                        >
                            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                            {error}
                        </div>
                    )}

                    {loading && (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                        </div>
                    )}

                    {!loading && filteredEvents.length > 0 && (
                        <div className="grid md:grid-cols-2 gap-4">
                            {filteredEvents.map(event => (
                                <EventCard
                                    key={event.id}
                                    event={event}
                                    isRegistered={myRegistrations.has(event.id)}
                                    onRegister={handleRegister}
                                    onWithdraw={handleWithdraw}
                                    isLoading={actionLoading === event.id}
                                    isLoggedIn={!!user}
                                    onViewRoster={(e) => setRosterEvent(e)}
                                    isOrganizer={user?.id === event.organizer_id}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}

                    {!loading && filteredEvents.length === 0 && (selectedFormat || selectedStatus) && (
                        <div className="text-center py-6 text-white/40 text-sm">
                            <p>No community events match your filters.</p>
                            <button
                                onClick={() => { setSelectedFormat(null); setSelectedStatus(null); }}
                                className="text-purple-400 hover:text-purple-300 text-sm mt-1"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}

                    {/* Event Types Info */}
                    <div className="p-4 bg-slate-800/30 rounded-xl border border-white/5">
                        <h3 className="text-sm font-medium text-white mb-3">Event Types</h3>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {Object.entries(EVENT_TYPES).map(([key, config]) => {
                                const Icon = config.icon;
                                return (
                                    <div key={key} className="flex items-center gap-2 text-xs text-white/60">
                                        <Icon className="w-4 h-4 text-purple-400" />
                                        <div>
                                            <div className="text-white/80">{config.label}</div>
                                            <div className="text-white/40 text-[10px]">{config.description}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}

            {/* Official Events - External Links */}
            <div className="p-4 bg-gradient-to-br from-purple-900/20 to-amber-900/10 rounded-xl border border-purple-500/20">
                <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-400" />
                    Find Official Events
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* Melee.gg */}
                    <a
                        href="https://melee.gg/Tournament/Index"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-purple-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-purple-300">Melee.gg</div>
                        <div className="text-xs text-white/40">RCQs, Regionals, Premiers</div>
                    </a>

                    {/* Magic.gg */}
                    <a
                        href="https://magic.gg/events"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-amber-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-amber-300">Magic.gg</div>
                        <div className="text-xs text-white/40">Pro Tour, MagicCon</div>
                    </a>

                    {/* Star City Games */}
                    <a
                        href="https://starcitygames.com/pages/scg-tour"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-blue-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-blue-300">SCG Tour</div>
                        <div className="text-xs text-white/40">Star City Games events</div>
                    </a>

                    {/* ChannelFireball */}
                    <a
                        href="https://channelfireball.com/all-strategy/tag/events/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-red-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-red-300">ChannelFireball</div>
                        <div className="text-xs text-white/40">CFB Events & GPs</div>
                    </a>

                    {/* WPN Locator */}
                    <a
                        href="https://locator.wizards.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-green-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-green-300">WPN Locator</div>
                        <div className="text-xs text-white/40">Local store events</div>
                    </a>

                    {/* SpellTable */}
                    <a
                        href="https://spelltable.wizards.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-pink-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-pink-300">SpellTable</div>
                        <div className="text-xs text-white/40">Online Commander</div>
                    </a>

                    {/* MTG Arena */}
                    <a
                        href="https://magic.wizards.com/en/mtgarena"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-orange-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-orange-300">MTG Arena</div>
                        <div className="text-xs text-white/40">Digital events & qualifiers</div>
                    </a>

                    {/* MTGO */}
                    <a
                        href="https://www.mtgo.com/en/mtgo"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-cyan-500/30 transition-all"
                    >
                        <div className="text-sm font-medium text-white group-hover:text-cyan-300">MTGO</div>
                        <div className="text-xs text-white/40">Challenges & Leagues</div>
                    </a>
                </div>
            </div>

            {/* Create Event Modal */}
            {user && (
                <CreateEventModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onCreated={(newEvent) => {
                        setEvents(prev => [newEvent, ...prev]);
                    }}
                    userId={user.id}
                    userName={userName}
                />
            )}

            {/* Register Modal (for decklist submission) */}
            {registerEvent && user && (
                <RegisterModal
                    isOpen={!!registerEvent}
                    onClose={() => setRegisterEvent(null)}
                    event={registerEvent}
                    userName={userName}
                    onRegistered={() => {
                        setMyRegistrations(prev => new Set([...prev, registerEvent.id]));
                        setEvents(prev => prev.map(e =>
                            e.id === registerEvent.id ? { ...e, current_players: e.current_players + 1 } : e
                        ));
                        setRegisterEvent(null);
                    }}
                />
            )}

            {/* Roster Modal */}
            {rosterEvent && (
                <RosterModal
                    isOpen={!!rosterEvent}
                    onClose={() => setRosterEvent(null)}
                    event={rosterEvent}
                />
            )}
        </div>
    );
}

export default EventsTab;
